import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "../Auth";
import {
  ChevronDownIcon,
  DoctorIcon,
  LogoutIcon,
  MenuIcon,
  SettingsIcon,
  PayrollIcon,
  ShieldIcon,
  ShieldCheckIcon,
  SupportIcon,
  GraduationCapIcon,
} from "../Icons";
import useCurrentUser from "../hooks/useCurrentUser";
import { APP_HEADER_H } from "./layoutConstants";
import { BRAND_THEME } from "../ui/brandTheme";

/** Shell chrome — brand (Patient indigo) tokens, shared by every menu item. */
const NAV_TILE =
  "w-7 h-7 rounded-md flex items-center justify-center border bg-indigo-50 border-indigo-100 text-indigo-600";
const NAV_ITEM = `w-full flex items-center gap-2.5 px-2.5 py-2 text-left text-slate-600 rounded-lg transition-colors hover:text-indigo-700 ${BRAND_THEME.menuHover}`;
const NAV_ITEM_ICON = "text-slate-400";

const LICENSE_VERIFICATION_URL =
  process.env.REACT_APP_LICENSE_VERIFICATION_URL ||
  "https://search.dca.ca.gov/";
const OIG_VERIFICATION_URL =
  process.env.REACT_APP_OIG_VERIFICATION_URL ||
  "https://exclusions.oig.hhs.gov/";

const Navbar = ({ toggleSidebar, sidebarOpen }) => {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const dropdownRef = useRef(null);
  const verificationRef = useRef(null);
  const { officeLogo, companyName } = useCurrentUser();

  const openExternalVerification = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setVerificationOpen(false);
  };

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
  };

  const handleProfile = () => {
    navigate("/profile");
    setDropdownOpen(false);
  };

  const handleManageSubscriptions = () => {
    navigate("/manage-subscriptions");
    setDropdownOpen(false);
  };

  const isAdmin = currentUser?.roleId === 1;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (
        verificationRef.current &&
        !verificationRef.current.contains(event.target)
      ) {
        setVerificationOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setDropdownOpen(false);
        setVerificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const logoSrc = officeLogo
    ? officeLogo.startsWith("http")
      ? officeLogo
      : `${typeof window !== "undefined" ? window.location.origin : ""}${
          officeLogo.startsWith("/") ? officeLogo : `/${officeLogo}`
        }`
    : `${typeof window !== "undefined" ? window.location.origin : ""}/login-logo.png`;

  return (
    <nav
      className={`bg-white border-b border-slate-200 ${APP_HEADER_H} px-3 sm:px-4 flex justify-between items-center sticky top-0 z-50 shrink-0 w-full`}
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <button
          type="button"
          className={`p-1.5 rounded-md transition-colors ${
            sidebarOpen
              ? "text-indigo-700 bg-indigo-50"
              : "text-slate-500 hover:text-indigo-600 hover:bg-slate-50"
          }`}
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          title={
            sidebarOpen
              ? "Hide sidebar"
              : "Show sidebar (or move cursor to left edge)"
          }
          aria-expanded={sidebarOpen}
          aria-controls="app-sidebar"
        >
          <MenuIcon size={20} />
        </button>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 rounded-md shrink-0"
          aria-label="Go to Dashboard"
        >
          <img
            src={logoSrc}
            alt={companyName || "Company Logo"}
            className="h-7 w-auto max-w-[130px] object-contain"
          />
        </button>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {currentUser && (
          <button
            type="button"
            onClick={() => {
              setVerificationOpen(false);
              setDropdownOpen(false);
              navigate("/training");
            }}
            className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-slate-50 transition-colors"
            aria-label="Open training"
          >
            <div className={NAV_TILE}>
              <GraduationCapIcon size={15} />
            </div>
            <div className="hidden lg:flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-800 leading-none">
                Training
              </span>
              <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                Videos &amp; guides
              </span>
            </div>
          </button>
        )}

        {currentUser && (
          <button
            type="button"
            onClick={() => {
              setVerificationOpen(false);
              setDropdownOpen(false);
              navigate("/support");
            }}
            className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-slate-50 transition-colors"
            aria-label="Open support"
          >
            <div className="w-7 h-7 bg-slate-50 rounded-md flex items-center justify-center border border-slate-200 text-[#1e3a5f]">
              <SupportIcon size={15} />
            </div>
            <div className="hidden lg:flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-800 leading-none">
                Support
              </span>
              <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                Help &amp; contact
              </span>
            </div>
          </button>
        )}

        {currentUser && (
          <div className="relative" ref={verificationRef}>
            <button
              type="button"
              onClick={() => {
                setVerificationOpen((prev) => !prev);
                setDropdownOpen(false);
              }}
              className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-slate-50 transition-colors"
              aria-expanded={verificationOpen}
              aria-haspopup="true"
              aria-label="Open verifications menu"
            >
              <div className={NAV_TILE}>
                <ShieldIcon size={15} />
              </div>
              <div className="hidden lg:flex flex-col items-start">
                <span className="text-xs font-semibold text-slate-800 leading-none">
                  Verifications
                </span>
                <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                  License &amp; OIG
                </span>
              </div>
              <ChevronDownIcon
                size={14}
                className={`hidden sm:block text-slate-400 transition-transform ${
                  verificationOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {verificationOpen && (
              <div
                className="absolute right-0 mt-1.5 w-72 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-[60]"
                role="menu"
                aria-label="Verification options"
              >
                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider px-4 pt-1.5 pb-1">
                  Verification
                </p>
                <div className="border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() =>
                      openExternalVerification(LICENSE_VERIFICATION_URL)
                    }
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-indigo-50/60 transition-colors"
                    role="menuitem"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <ShieldIcon size={18} className="text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">
                        License Verification
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Verify professional licenses and credentials
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      openExternalVerification(OIG_VERIFICATION_URL)
                    }
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-indigo-50/60 transition-colors"
                    role="menuitem"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <ShieldCheckIcon size={18} className="text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">
                        OIG Verification
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Search OIG exclusion list
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentUser && (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(!dropdownOpen);
                setVerificationOpen(false);
              }}
              className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-slate-50 transition-colors"
            >
              <div className={NAV_TILE}>
                <DoctorIcon size={15} />
              </div>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-xs font-semibold text-slate-800 leading-none">
                  {currentUser.firstName} {currentUser.lastName}
                </span>
                <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                  {currentUser.role?.name || "Member"}
                </span>
              </div>
              <ChevronDownIcon
                size={14}
                className={`hidden sm:block text-slate-400 transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-1.5 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-50">
                <div className="px-3 py-2 border-b border-slate-100 mb-0.5">
                  <p className="text-xs font-semibold text-slate-900">
                    Signed in as
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {currentUser.email}
                  </p>
                </div>

                <div className="p-1">
                  {isAdmin && (
                    <button
                      type="button"
                      className={NAV_ITEM}
                      onClick={handleManageSubscriptions}
                    >
                      <PayrollIcon size={16} className={NAV_ITEM_ICON} />
                      <span className="text-sm font-medium">Manage Plans</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleProfile}
                    className={NAV_ITEM}
                  >
                    <DoctorIcon size={16} className={NAV_ITEM_ICON} />
                    <span className="text-sm font-medium">My Profile</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/change-password");
                      setDropdownOpen(false);
                    }}
                    className={NAV_ITEM}
                  >
                    <SettingsIcon size={16} className={NAV_ITEM_ICON} />
                    <span className="text-sm font-medium">Account Settings</span>
                  </button>
                </div>

                <div className="border-t border-slate-100 my-0.5" />

                <div className="p-1">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogoutIcon size={16} className="text-red-400" />
                    <span className="text-sm font-medium">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
